import { Button, InputBar } from '~/ui';
import CustomSelect from '~/components/Shared/CustomSelect/CustomSelect';
import { FastField, Formik, FormikHelpers, useFormikContext } from 'formik';
import CloseIcon from '~/assets/svg/newSvg/close-cross.svg?react';
import i18next, { t } from 'i18next';
import styles from './UserActivityParams.module.scss';
import cn from 'classnames';

interface ICondition {
  display_name: string;
  name: string;
  id: number;
}

export interface IFormikValues {
  entrance_quantity: {
    id: string;
    condition: ICondition;
    value: string;
  }[];
}

interface ConditionProps {
  id: string;
  options?: { id: number; display_name: string; name: string };
  t: (key: string) => string;
  handleDeleteCondition: (id: string) => void;
}

interface IUserActivityProps {
  onClick: () => void;
  initialValues: IFormikValues;
  onSubmit: (values: IFormikValues) => void;
  handleAddCondition: (
    values: IFormikValues,
    setFieldValue: FormikHelpers<IFormikValues>['setFieldValue']
  ) => void;
  handleDeleteCondition: (
    id: string,
    values: IFormikValues,
    setFieldValue: FormikHelpers<IFormikValues>['setFieldValue']
  ) => void;
}

const Condition = ({ id, t, handleDeleteCondition }: ConditionProps) => {
  const { values, setFieldValue } = useFormikContext<any>();

  const optionsNew = [
    { id: 1, display_name: i18next.t('more'), name: '>' },
    { id: 2, display_name: i18next.t('less'), name: '<' },
    { id: 3, display_name: i18next.t('equal'), name: '=' },
    { id: 4, display_name: i18next.t('more_or_equal'), name: '>=' },
    { id: 5, display_name: i18next.t('less_or_equal'), name: '<=' },
  ];

  const index = values.entrance_quantity.findIndex(
    (item: any) => item.id === id
  );
  if (index === -1) return null;

  return (
    <div className={styles.paramsWrapper}>
      <CustomSelect
        value={values?.entrance_quantity[index]?.condition}
        options={optionsNew}
        placeholder={t('select_operator')}
        onChange={value =>
          setFieldValue(`entrance_quantity[${index}].condition`, value)
        }
        className={styles.select}
      />

      <FastField name={`entrance_quantity[${index}].value`}>
        {({ field, meta, form }: any) => (
          <InputBar
            {...field}
            error={meta.touched && !!meta.error}
            errors={meta.error}
            classNameInputWrapper={styles.input}
            placeholder={t('enter_the_number')}
            onChange={e => {
              const onlyNumbers = e.target.value.replace(/\D/g, '');
              form.setFieldValue(field.name, onlyNumbers);
            }}
            label=""
          />
        )}
      </FastField>

      <button
        type="button"
        className={styles.paramsCloseBtn}
        onClick={() => handleDeleteCondition(id)}
      >
        <CloseIcon className={styles.paramsCloseIcon} />
      </button>
    </div>
  );
};

export const UserActivityParams = ({
  onClick,
  initialValues,
  onSubmit,
  handleAddCondition,
  handleDeleteCondition,
}: IUserActivityProps) => {
  return (
    <Formik<IFormikValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize={true}
    >
      {({ values, setFieldValue, handleSubmit }) => (
        <div className={styles.formWrapper}>
          <form onSubmit={handleSubmit} className={styles.form}>
            <p className={styles.formTitle}>{t('select_number_inputs')}</p>

            <div className={styles.entranceWrapper}>
              {!!values.entrance_quantity.length && (
                <p className={styles.entranceTitle}>{t('entrance_quantity')}</p>
              )}
              {values.entrance_quantity.map(item => (
                <Condition
                  key={item.id}
                  id={item.id}
                  t={t}
                  handleDeleteCondition={id =>
                    handleDeleteCondition(id, values, setFieldValue)
                  }
                />
              ))}
              {values.entrance_quantity.length < 6 && (
                <button
                  type="button"
                  onClick={() => handleAddCondition(values, setFieldValue)}
                  className={styles.parametrAddBtn}
                >
                  + {t('add_parameter')}
                </button>
              )}
            </div>

            <div className={styles.btnWrapper}>
              <Button
                type="button"
                text={t('close')}
                onClick={onClick}
                className={cn(styles.btn, styles.closeBtn)}
              />
              <Button
                type="submit"
                text={t('apply')}
                className={cn(styles.btn, styles.submitBtn)}
                disabled={
                  (values.entrance_quantity.length === 0 &&
                    initialValues.entrance_quantity.length === 0) ||
                  values.entrance_quantity.some(
                    item => !item.condition.name || !item.value.trim()
                  )
                }
              />
            </div>
          </form>
          <button
            type="button"
            className={styles.closeFormBtn}
            onClick={onClick}
          >
            <CloseIcon className={styles.closeBntIcon} />
          </button>
        </div>
      )}
    </Formik>
  );
};
